import React from 'react';
import { ArrowRight, ArrowLeft, Check, Facebook, Instagram, Linkedin, Youtube, TrendingUp, Target, PenTool, BarChart3, Users, MessageCircle, Calendar, Eye, Megaphone, Building2, Briefcase, User, MapPin, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CookieBanner from '../components/layout/CookieBanner';

const trustPoints = [
  'Results-driven strategy',
  'Content + ads done for you',
  'Platform-specific expertise',
  'Clear ROI & reporting'
];

const whoWeHelp = [
  { icon: Building2, title: 'Small & Medium Businesses' },
  { icon: TrendingUp, title: 'B2B & B2C Brands' },
  { icon: MapPin, title: 'Local & Service-Based Businesses' },
  { icon: User, title: 'Coaches, Consultants & Creators' },
  { icon: Sparkles, title: 'SaaS & Online Businesses' }
];

const approach = [
  'Audience targeting & positioning',
  'High-engagement content strategy',
  'Paid social media advertising',
  'Lead generation & funnel integration',
  'Analytics, tracking, and optimization'
];

const services = [
  {
    icon: Target,
    title: 'Social Media Strategy',
    description: 'A clear roadmap built around your business goals, audience, and platforms.'
  },
  {
    icon: PenTool,
    title: 'Content Creation & Posting',
    description: 'Scroll-stopping content designed to educate, engage, and convert.'
  },
  {
    icon: Megaphone,
    title: 'Paid Social Media Advertising',
    description: 'Highly targeted Facebook, Instagram, and LinkedIn ads that drive leads and sales.'
  },
  {
    icon: Users,
    title: 'Community Growth & Engagement',
    description: 'We help you build trust and authority by engaging with the right audience.'
  },
  {
    icon: TrendingUp,
    title: 'Lead Generation Campaigns',
    description: 'We turn social platforms into consistent lead sources using proven funnels.'
  },
  {
    icon: BarChart3,
    title: 'Analytics & Reporting',
    description: 'Clear reporting that shows what\'s working, what\'s not, and how to scale.'
  }
];

const managementServices = [
  {
    icon: Calendar,
    title: 'Content Strategy & Planning',
    description: 'We create a monthly content plan tailored to your brand, audience, and goals.'
  },
  {
    icon: PenTool,
    title: 'Content Creation',
    description: 'Professional graphics, videos, and captions designed to engage and educate your audience.'
  },
  {
    icon: Eye,
    title: 'Posting & Scheduling',
    description: 'Consistent posting at optimal times across your chosen platforms.'
  },
  {
    icon: MessageCircle,
    title: 'Community Engagement',
    description: 'We monitor and engage with comments, messages, and followers to build trust.'
  },
  {
    icon: Sparkles,
    title: 'Brand Voice & Consistency',
    description: 'Your brand stays consistent across all platforms with a clear voice and message.'
  },
  {
    icon: BarChart3,
    title: 'Analytics & Reporting',
    description: 'Monthly reports that show growth, engagement, and opportunities to improve.'
  }
];

const platforms = [
  { name: 'Facebook', color: 'bg-blue-600' },
  { name: 'Instagram', color: 'bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400' },
  { name: 'LinkedIn', color: 'bg-blue-700' },
  { name: 'TikTok', color: 'bg-black' },
  { name: 'YouTube Shorts', color: 'bg-red-600' }
];

const whyChooseUs = [
  'Business-driven strategy, not vanity metrics',
  'Content + ads fully managed',
  'Conversion-focused messaging',
  'Transparent communication & reporting',
  'Continuous optimization for better ROI'
];

const process = [
  {
    step: '01',
    title: 'Discovery & Audit',
    description: 'We analyze your brand, competitors, audience, and current performance.'
  },
  {
    step: '02',
    title: 'Strategy & Planning',
    description: 'We build a custom social media plan aligned with your goals.'
  },
  {
    step: '03',
    title: 'Content & Campaign Creation',
    description: 'We create content and ads designed to perform.'
  },
  {
    step: '04',
    title: 'Launch & Optimize',
    description: 'We launch campaigns and continuously optimize for better results.'
  },
  {
    step: '05',
    title: 'Scale What Works',
    description: 'We double down on top-performing content and ads to drive growth.'
  }
];

const results = [
  'Increased brand awareness with the right audience',
  'Higher engagement and follower growth',
  'Consistent lead flow or sales',
  'Clear visibility into ROI'
];

const SocialMediaMarketing = () => {
  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[85vh] flex flex-col justify-center overflow-hidden bg-[#f8f7f4] pt-32 pb-16">
          {/* Gradient blob background */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div
              className="absolute top-0 right-0 w-[80%] h-[120%] opacity-50"
              style={{
                background: `
                  radial-gradient(ellipse 70% 50% at 75% 20%, rgba(236, 72, 153, 0.3) 0%, transparent 50%),
                  radial-gradient(ellipse 60% 50% at 85% 50%, rgba(168, 85, 247, 0.25) 0%, transparent 50%),
                  radial-gradient(ellipse 50% 60% at 65% 70%, rgba(251, 146, 60, 0.2) 0%, transparent 50%)
                `,
                filter: 'blur(60px)',
                transform: 'rotate(-10deg)'
              }}
            />
          </div>

          <div className="relative z-10 px-6 md:px-12 lg:px-24">
            {/* Back link */}
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-gray-600 hover:text-black transition-colors mb-12"
            >
              <ArrowLeft size={16} /> Back to Home
            </Link>

            <div className="max-w-4xl">
              <span className="text-sm uppercase tracking-widest text-gray-500 mb-4 block">Social Media Marketing</span>
              <h1 className="text-[clamp(2.5rem,5vw,4.5rem)] font-light leading-[1.1] text-black mb-6">
                Turn Social Media Into a{' '}
                <span className="font-medium">Revenue Engine</span>
              </h1>
              <p className="text-xl text-gray-700 max-w-2xl mb-4">
                Strategic social media marketing that drives leads, sales, and real business growth — not just likes.
              </p>
              <p className="text-lg text-gray-600 max-w-2xl mb-8">
                We help businesses attract the right audience, build trust, and convert attention into customers 
                across Facebook, Instagram, LinkedIn, and more.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white rounded-full font-medium hover:bg-gray-800 transition-colors"
                >
                  Get a Free Strategy Call <ArrowRight size={18} />
                </a>
                <a
                  href="#how-it-works"
                  className="inline-flex items-center gap-2 px-8 py-4 border border-black text-black rounded-full font-medium hover:bg-black hover:text-white transition-colors"
                >
                  See How It Works
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Strip */}
        <section className="py-8 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="flex flex-wrap justify-center gap-8 md:gap-16">
              {trustPoints.map((point, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Check size={18} className="text-green-400" />
                  <span className="text-sm md:text-base">{point}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Who We Help */}
        <section className="py-24 bg-[#f8f7f4]">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                Who We Help
              </h2>
              <p className="text-lg text-gray-600">
                We work with businesses that want social media to actually move the needle.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-12">
              {whoWeHelp.map((item, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
                  <item.icon className="w-10 h-10 text-gray-700 mx-auto mb-4" />
                  <h3 className="text-sm font-medium text-black">{item.title}</h3>
                </div>
              ))}
            </div>

            <p className="text-center text-lg text-gray-700 font-medium">
              If you're posting consistently but not seeing results, we fix that.
            </p>
          </div>
        </section>

        {/* What We Do */}
        <section id="how-it-works" className="py-24 bg-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                  Social Media That Converts Attention Into Action
                </h2>
                <p className="text-lg text-gray-600 mb-4">
                  Social media isn't about going viral — it's about getting the right message 
                  in front of the right people at the right time.
                </p>
                <p className="text-gray-700 mb-8">
                  We combine strategy, content, and paid promotion to build predictable growth on social.
                </p>
                <div className="mb-8">
                  <p className="text-gray-700 font-medium mb-4">Our approach focuses on:</p>
                  <ul className="space-y-3">
                    {approach.map((item, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <Check size={18} className="text-green-500 flex-shrink-0" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-lg text-black font-medium">
                  Everything we do is tied to measurable outcomes.
                </p>
              </div>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/8nud5zav_Screenshot%202025-12-18%20at%202.24.13%E2%80%AFPM.png" 
                    alt="Social Media Analytics Dashboard showing reach and engagement"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Analytics Showcase */}
        <section className="py-24 bg-gray-50">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                Real Results. Real Analytics.
              </h2>
              <p className="text-lg text-gray-600">
                We track every metric that matters and provide transparent reporting so you can see exactly how your social media is performing.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="rounded-2xl overflow-hidden shadow-xl bg-white">
                <img 
                  src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/hg5fe9ti_Screenshot%202025-12-18%20at%202.24.21%E2%80%AFPM.png" 
                  alt="Facebook Page Insights showing reach and engagement metrics"
                  className="w-full h-auto object-cover"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-xl bg-white">
                <img 
                  src="https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/5yt6cvn3_Screenshot%202025-12-18%20at%202.24.30%E2%80%AFPM.png" 
                  alt="Social Media Performance Dashboard"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Marketing Services */}
        <section className="py-24 bg-[#f8f7f4]">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="mb-16">
              <h2 className="text-[clamp(3rem,8vw,8rem)] font-light text-gray-300 leading-none tracking-tight">
                <span className="block">MARKETING</span>
                <span className="block ml-[10%]">SERVICES</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-all group">
                  <service.icon className="w-10 h-10 text-gray-400 group-hover:text-pink-500 mb-4 transition-colors" />
                  <h3 className="text-xl font-medium text-black mb-3">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Management Services */}
        <section className="py-24 bg-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                Social Media Management
              </h2>
              <p className="text-lg text-gray-600">
                More than posting — it's brand building. We handle everything so you can focus on running your business.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {managementServices.map((service, index) => (
                <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:bg-gray-100 transition-all group">
                  <service.icon className="w-10 h-10 text-gray-400 group-hover:text-purple-500 mb-4 transition-colors" />
                  <h3 className="text-xl font-medium text-black mb-3">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Platforms */}
        <section className="py-24 bg-[#f8f7f4]">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                Platforms We Specialize In
              </h2>
              <p className="text-lg text-gray-600">
                We tailor strategy and content to each platform — no copy-and-paste posting.
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-6">
              {platforms.map((platform, index) => (
                <div key={index} className={`${platform.color} rounded-full px-8 py-4 text-white font-medium shadow-lg hover:scale-105 transition-transform`}>
                  {platform.name}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-24 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-medium mb-6">
                  Strategy First. <span className="text-gray-500">Results Always.</span>
                </h2>
                <p className="text-gray-400 text-lg mb-8">
                  Most agencies focus on posting content. We focus on outcomes.
                </p>
                <div className="mb-8">
                  <p className="text-white font-medium mb-4">What makes us different:</p>
                  <ul className="space-y-3">
                    {whyChooseUs.map((item, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <Check size={18} className="text-green-400 flex-shrink-0" />
                        <span className="text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-xl text-white font-medium">
                  Your growth is the goal — everything else supports that.
                </p>
              </div>
              <div className="relative">
                <div className="bg-gradient-to-br from-pink-500/20 via-purple-500/20 to-blue-500/20 rounded-3xl p-8">
                  <div className="space-y-4">
                    <div className="bg-gray-800 rounded-xl p-4 flex items-center gap-4">
                      <div className="w-12 h-12 bg-pink-500 rounded-full flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-white font-medium">Engagement Rate</p>
                        <p className="text-green-400">+340% vs industry avg</p>
                      </div>
                    </div>
                    <div className="bg-gray-800 rounded-xl p-4 flex items-center gap-4">
                      <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center">
                        <Target className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-white font-medium">Cost Per Lead</p>
                        <p className="text-green-400">-62% reduction</p>
                      </div>
                    </div>
                    <div className="bg-gray-800 rounded-xl p-4 flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                        <BarChart3 className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-white font-medium">Return on Ad Spend</p>
                        <p className="text-green-400">5.8x average ROAS</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Process */}
        <section className="py-24 bg-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mb-16">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-6">
                Our Process
              </h2>
            </div>

            <div className="grid md:grid-cols-5 gap-8">
              {process.map((item, index) => (
                <div key={index} className="relative">
                  <span className="text-6xl font-light text-gray-200">{item.step}</span>
                  <h3 className="text-lg font-medium text-black mt-2 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                  {index < process.length - 1 && (
                    <div className="hidden md:block absolute top-8 right-0 w-full h-px bg-gray-200 -mr-4"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="py-24 bg-[#f8f7f4]">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-medium text-black mb-12">
                Results You Can Expect
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {results.map((result, index) => (
                  <div key={index} className="bg-white rounded-2xl p-6 shadow-sm flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Check size={20} className="text-green-600" />
                    </div>
                    <p className="text-gray-800 font-medium text-left">{result}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section id="contact" className="py-24 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-light mb-6">
                Ready to Turn Social Media Into a{' '}
                <span className="font-medium">Growth Channel?</span>
              </h2>
              <p className="text-gray-400 text-lg mb-8">
                Let's build a social media strategy that actually drives results.
              </p>
              <a
                href="mailto:hello@gomobyle.com"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-black rounded-full font-medium hover:bg-gray-200 transition-colors mb-4"
              >
                Book a Free Strategy Call <ArrowRight size={18} />
              </a>
              <p className="text-gray-500 text-sm">
                No contracts. No guesswork. Just growth.
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <CookieBanner />
    </div>
  );
};

export default SocialMediaMarketing;
